/**
 * Service Health Endpoints Configuration
 *
 * Defines the services to monitor and their health endpoint URLs.
 * Used by the Admin dashboard to display service health status.
 */

import { getFrontendUrlBuilder } from '../utils/url-builder';

export interface ServiceEndpoint {
  serviceId: string;      // Unique service identifier
  serviceName: string;    // Display name for UI
  healthUrl: string;      // Health endpoint URL
  icon?: string;          // Optional icon (future enhancement)
  order: number;          // Display order in dashboard
  description?: string;   // Service description
}

/**
 * List of all services to monitor
 *
 * URLs are constructed using URL builder for environment-aware configuration
 */
const urlBuilder = getFrontendUrlBuilder();
const isDevelopment = import.meta.env.DEV;

/**
 * Get API public URL from environment variable or build from API URL
 * In production, use VITE_API_PUBLIC_URL if set, otherwise construct from VITE_API_URL
 */
function getApiPublicUrl(): string {
  if (isDevelopment) {
    return urlBuilder.buildApiUrl('/api/health');  // API health now at /api/health not /health
  }

  // In production, try VITE_API_PUBLIC_URL first, fallback to VITE_API_URL
  const apiPublicUrl = import.meta.env.VITE_API_PUBLIC_URL || import.meta.env.VITE_API_URL;
  if (apiPublicUrl) {
    return `${apiPublicUrl}/api/health`;  // Must use /api/health to match nginx proxy rules
  }

  // Last resort: use relative path (will hit nginx, not ideal but won't break)
  return '/api/health';
}

export const SERVICE_ENDPOINTS: ServiceEndpoint[] = [
  {
    serviceId: 'api',
    serviceName: 'API Service',
    healthUrl: getApiPublicUrl(),
    order: 1,
    description: 'Business logic tier with REST endpoints'
  },
  {
    serviceId: 'storage',
    serviceName: 'Storage Service',
    healthUrl: isDevelopment
      ? urlBuilder.buildStorageHealthUrl()
      : '/api/proxy/storage/health',
    order: 2,
    description: 'Blob storage abstraction (S3-equivalent)'
  },
  {
    serviceId: 'database',
    serviceName: 'Database Service',
    healthUrl: isDevelopment
      ? urlBuilder.buildDatabaseHealthUrl()
      : '/api/proxy/database/health',
    order: 3,
    description: 'Data persistence layer (PostgreSQL)'
  },
  {
    serviceId: 'frontend',
    serviceName: 'Frontend',
    healthUrl: isDevelopment
      ? urlBuilder.buildFrontendHealthUrl()
      : '/health',
    order: 4,
    description: 'React-based web UI'
  }
];

/**
 * Get endpoint configuration by service ID
 */
export function getServiceEndpoint(serviceId: string): ServiceEndpoint | undefined {
  return SERVICE_ENDPOINTS.find(endpoint => endpoint.serviceId === serviceId);
}

/**
 * Get all service endpoints sorted by display order
 */
export function getAllServiceEndpoints(): ServiceEndpoint[] {
  return [...SERVICE_ENDPOINTS].sort((a, b) => a.order - b.order);
}
